import { useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { type BaseModalFCProps } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import TextArea from '@/components/ui/TextArea';

export const DataExportRequestModal = ({
  open,
  onOpenChange,
}: BaseModalFCProps) => {
  const [reason, setReason] = useState('');
  const [timeFrame, setTimeFrame] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim() || !timeFrame.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await backendApis.feedback.submitDataExportRequest(reason, timeFrame);
      onOpenChange(false);
      toast.info('Your data export request has been submitted.');
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <YStack className="gap-4">
            <Dialog.Header>
              <Dialog.Title className="text-lg text-foreground">
                Data Export Request
              </Dialog.Title>
              <Dialog.Description>
                Please describe the data you would like exported and the time
                frame.
              </Dialog.Description>
            </Dialog.Header>

            <YStack className="gap-4">
              <YStack className="gap-2">
                <Label htmlFor="reason">Reason for request</Label>
                <TextArea
                  id="reason"
                  placeholder="Describe why you are requesting a data export..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="h-24"
                  autoFocus
                />
              </YStack>

              <YStack className="gap-2">
                <Label htmlFor="time-frame">Time frame</Label>
                <Input
                  id="time-frame"
                  placeholder="e.g. Last 6 months, Jan 2024 - Dec 2024"
                  value={timeFrame}
                  onChange={(e) => setTimeFrame(e.target.value)}
                />
              </YStack>
            </YStack>

            <Dialog.Footer>
              <XStack className="gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!reason.trim() || !timeFrame.trim() || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              </XStack>
            </Dialog.Footer>
          </YStack>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
